import React, { useState, useEffect } from "react";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import Layout, { Container } from "../components/layout";
import { apiurl } from "../config/config";
import {
  FaFileExcel,
  FaFilter,
  FaPlus,
  FaSearch,
  FaUserCheck,
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import { motion } from "framer-motion";
import { IoMdClose } from "react-icons/io";
import { SuwasthiLoader } from "../components/loader";
import Pagination from "../components/pagination";
import AssignRoleModal from "../components/assignRoleModal";
import Breadcrumb from "../components/breadcrumb";
import { useUser } from "../config/userProvider";
import { hasPermission } from "../config/permissions";

const FilterApplications = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [assignModal, setAssignModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [role, setRole] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [paginationData, setPaginationData] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  // removed unused `page` state for clarity
  const [filterStatus, setFilterStatus] = useState([]); // <-- now an array
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    // Sync URL -> state (only set if different to avoid loops)
    const qSearch = searchParams.get("search") || "";
    const qStartDate = searchParams.get("startDate")
      ? new Date(searchParams.get("startDate"))
      : null;
    const qEndDate = searchParams.get("endDate")
      ? new Date(searchParams.get("endDate"))
      : null;

    // ✅ Fix: handle single & multiple applicationStatus
    let qStatuses = searchParams.getAll("applicationStatus");
    if (qStatuses.length === 0 && searchParams.get("applicationStatus")) {
      qStatuses = [searchParams.get("applicationStatus")];
    }

    const qPage = Number(searchParams.get("page") || 1);
    const qLimit = Number(searchParams.get("limit") || itemsPerPage);

    if (qSearch !== search) setSearch(qSearch);
    if (
      (qStartDate && !startDate) ||
      (qStartDate &&
        startDate &&
        qStartDate.toISOString() !== startDate.toISOString())
    )
      setStartDate(qStartDate);
    if (
      (qEndDate && !endDate) ||
      (qEndDate && endDate && qEndDate.toISOString() !== endDate.toISOString())
    )
      setEndDate(qEndDate);

    // ✅ Fix: update statuses properly
    if (JSON.stringify(qStatuses) !== JSON.stringify(filterStatus)) {
      setFilterStatus(qStatuses);
    }

    if (qPage !== currentPage) setCurrentPage(qPage);
    if (qLimit !== itemsPerPage) setItemsPerPage(qLimit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    // Sync state -> URL (replace to avoid history spam)
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (startDate)
      params.set("startDate", startDate.toISOString().split("T")[0]);
    if (endDate) params.set("endDate", endDate.toISOString().split("T")[0]);
    if (filterStatus && filterStatus.length) {
      filterStatus.forEach((s) => params.append("applicationStatus", s));
    }
    if (currentPage && currentPage !== 1)
      params.set("page", String(currentPage));
    if (itemsPerPage && itemsPerPage !== 100)
      params.set("limit", String(itemsPerPage));
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, startDate, endDate, filterStatus, currentPage, itemsPerPage]);

  // helper to format date as yyyy-mm-dd
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-CA");
  };

  const fetchFilterApplications = () => {
    const hasFilters =
      search.trim() ||
      startDate ||
      endDate ||
      (filterStatus && filterStatus.length > 0);

    if (!hasFilters) {
      setApplications([]);
      setTotalItems(0);
      setTotalPages(1);
      return;
    }

    setLoading(true);
    const params = new URLSearchParams();
    params.append("page", currentPage);
    params.append("limit", itemsPerPage);

    if (startDate) params.append("startDate", formatDate(startDate));
    if (endDate) params.append("endDate", formatDate(endDate));
    if (filterStatus.length)
      filterStatus.forEach((s) => params.append("applicationStatus", s));
    if (search) params.append("search", search);

    fetch(`${apiurl}/api/application/advanced-search?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        setApplications(data.applications);
        setTotalItems(data.pagination?.totalItems || 0);
        setTotalPages(data.totalPages || 0);
        setPaginationData(data.pagination || {});
        setSelectedApplications([]);
        setSelectAll(false);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching applications:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchFilterApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, search, startDate, endDate, filterStatus]);

  // rest of your existing helpers (handleAssign, handleView, checkbox logic, exportToExcel, etc.)
  const handleAssign = (app) => {
    setSelectedApp(app);
    setAssignModal(true);
  };

  const handleView = async (appId, isRead) => {
    if (!isRead) {
      await fetch(`${apiurl}/api/application/read-status`, {
        method: "PATCH",
        body: JSON.stringify({ applicationObjId: appId, isRead: true }),
        headers: { "Content-Type": "application/json" },
      });

      setApplications((prev) =>
        prev.map((app) => (app._id === appId ? { ...app, isRead: true } : app))
      );
    }
    navigate(`/applications/${appId}`);
  };

  const filteredApps = applications.filter((app) => {
    const nameMatch =
      app?.candidateSnapshot?.firstName
        ?.toLowerCase()
        .includes(search.toLowerCase()) ||
      app?.candidateSnapshot?.lastName
        ?.toLowerCase()
        .includes(search.toLowerCase());
    const emailMatch = app?.candidateSnapshot?.email
      ?.toLowerCase()
      .includes(search.toLowerCase());
    return nameMatch || emailMatch;
  });

  const listVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: { delay: index * 0.1, duration: 0.4 },
    }),
  };

  const handleLimitChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  useEffect(() => {
    setTotalPages(Math.ceil(totalItems / itemsPerPage) || 1);
  }, [totalItems, itemsPerPage]);

  const handleCheckboxChange = (appId) => {
    setSelectedApplications((prevSelected) =>
      prevSelected.includes(appId)
        ? prevSelected.filter((id) => id !== appId)
        : [...prevSelected, appId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedApplications([]);
    } else {
      setSelectedApplications(applications.map((app) => app._id));
    }
    setSelectAll(!selectAll);
  };

  // exportToExcel unchanged, kept as-is (you already have good masking/formatting)
  const exportToExcel = () => {
    // copy your existing exportToExcel function here (kept for brevity)
    let dataToExport = applications;

    if (selectedApplications.length > 0) {
      dataToExport = dataToExport.filter((emp) =>
        selectedApplications.includes(emp._id)
      );
    }

    if (dataToExport.length === 0) {
      alert("No employee data found for export.");
      return;
    }

    const uppercase = (value) => {
      return typeof value === "string" ? value.toUpperCase() : value;
    };

    const capitalize = (value) => {
      return typeof value === "string"
        ? value
            .split(/(\s+)/)
            .map((word) =>
              word.trim()
                ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                : word
            )
            .join("")
        : value;
    };

    const normalizeId = (rawId) => {
      if (!rawId) return "";
      const match = rawId.toUpperCase().match(/(SUW)[\s\-]?(\d+)/);
      return match ? `${match[1]}${match[2]}` : rawId.toUpperCase();
    };
    const maskPhone = (phone) => {
      if (!phone) return "";
      const strPhone = phone.toString();
      return strPhone.length > 4
        ? "*".repeat(strPhone.length - 4) + strPhone.slice(-4)
        : strPhone;
    };

    const maskEmail = (email) => {
      if (!email || !email.includes("@")) return email || "";
      const [local, domain] = email.split("@");
      const maskedLocal = local
        .split("")
        .map((ch, idx) => (idx % 2 === 0 ? ch : "*"))
        .join("");
      return `${maskedLocal}@${domain}`;
    };

    const flattenEmployeeData = (emp, index) => {
      const refType = emp.referenceDetails?.referenceType;
      let referenceName = "";
      let referenceId = "";

      if (refType === "employee") {
        referenceName = `EMP-${emp.referenceDetails.employeeName || ""}`;
        referenceId = normalizeId(emp.referenceDetails.employeeId || "");
      } else if (refType === "recruiter") {
        referenceName = `HR-${emp.referenceDetails.recruiterName || ""}`;
        referenceId = `SUW-${emp.referenceDetails.recruiterId || ""}`;
      } else if (refType === "consultancy") {
        referenceName = `${emp.referenceDetails.consultancyReference || ""}`;
        referenceId = "Consultancy";
      } else if (refType === "other") {
        referenceName = `OTHER-${emp.referenceDetails.otherReference || ""}`;
        referenceId = "N/A";
      } else if (refType === "walkin") {
        referenceName = `WALKIN`;
        referenceId = "N/A";
      } else {
        referenceName = "N/A";
        referenceId = "N/A";
      }

      const interviewDate = emp.screeningDetails?.date;
      const joiningDate = emp.offerDetails?.joiningDate;
      const joiningMailSent = emp.offerDetails?.joiningMail?.status;
      const joiningMailDate = emp.offerDetails?.joiningMail?.date;

      const offeredCTC = emp.offerDetails?.offeredCTC
        ? `${emp.offerDetails.offeredCTC.toFixed(2)} LPA / ${(
            emp.offerDetails.offeredCTC / 12
          ).toFixed(2)} MONTHLY`
        : "";
      const monthlySalary = emp.offerDetails?.monthlySalary
        ? `${emp.offerDetails.monthlySalary.toLocaleString("en-IN")} MONTHLY`
        : "";

      return {
        SNO: index + 1,
        ID: capitalize(emp.id || ""),
        NAME: capitalize(
          `${emp.candidateSnapshot.firstName} ${emp.candidateSnapshot.lastName}` ||
            ""
        ),
        DESIGNATION: capitalize(emp.positionTitle || ""),
        DEPARTMENT: capitalize(emp.department || ""),
        "REFERENCE NAME": capitalize(referenceName),
        "REFERENCE ID": uppercase(referenceId),
        "CONTACT NO": maskPhone(emp.candidateSnapshot.phone || ""),
        EMAIL: maskEmail(emp.candidateSnapshot.email || ""),
        MONTH: interviewDate
          ? new Date(interviewDate)
              .toLocaleString("default", { month: "long" })
              .toUpperCase()
          : "",
        YEAR: interviewDate ? new Date(interviewDate).getFullYear() : "",
        "TOTAL EXPERIENCE": emp.professionalInfo.totalExperience || 0,
        "RELEVANT EXPERIENCE": emp.professionalInfo.relevantExperience || 0,
        "PREVIOUS COMPANY": capitalize(
          emp.professionalInfo.previousCompany || ""
        ),
        PROCESS: capitalize(emp.professionalInfo.process || ""),
        "REASON FOR LEAVING": capitalize(
          emp.professionalInfo.reasonForLeaving || ""
        ),
        "INTERVIEW DATE": interviewDate
          ? new Date(interviewDate).toLocaleDateString().toUpperCase()
          : "",
        "INTERVIEW STATUS": capitalize(emp.applicationStatus || ""),
        "INTERVIEW REMARKS": capitalize(emp.screeningDetails?.feedback || ""),
        "OFFERED SALARY": offeredCTC,
        "MONTHLY SALARY": monthlySalary,
        "JOINING MAIL STATUS": joiningMailSent ? "SENT" : "NOT SENT",
        "JOINING MAIL DATE": joiningMailDate
          ? new Date(joiningMailDate).toLocaleDateString().toUpperCase()
          : "",
        "DATE OF JOINING": joiningDate
          ? new Date(joiningDate).toLocaleDateString().toUpperCase()
          : "",
      };
    };

    const formattedData = dataToExport.map(flattenEmployeeData);

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "APPLICATIONS");

    const fileName = `APPLICATIONS_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const { userData } = useUser();

  const hasReadPermission = (permissions, resourceKey) => {
    const perm = permissions?.find((p) => p.resource === resourceKey);
    return perm?.read === true;
  };

  const allStatuses = [
    "applied",
    "shortlisted",
    "screening",
    "interviewing",
    "training",
    "selected",
    "offered",
    "on-hold",
    "rejected",
    "hired",
    "joined",
    "absconded",
    "onboarding",
    "not-joined",
    "draft",
  ];

  useEffect(() => {
    if (!startDate && !endDate) {
      const today = new Date();
      setStartDate(today);
      setEndDate(today);
    }
  }, []); // run once on mount

  return (
    <Layout>
      <Container overflow={"overflow-scroll"}>
        <div className="p-2 bg-gray-100 ">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2 gap-3">
            <div className="flex items-center gap-2 w-full md:w-1/4">
              <input
                type="text"
                placeholder="Search by name, email, id, or aadhar"
                className="p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  // ✅ Allow only letters and numbers
                  if (/^[a-zA-Z0-9]*$/.test(value)) {
                    setSearch(value);
                  }
                }}
              />
            </div>
            <div className="flex items-center gap-2 w-full md:w-1/4">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                className="p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholderText="Start Date"
                dateFormat="yyyy-MM-dd"
              />
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                className="p-2 border rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholderText="End Date"
                dateFormat="yyyy-MM-dd"
              />
            </div>

            <div className="flex flex-wrap gap-2 justify-start md:justify-end items-center w-full md:w-auto">
              <div className="text-blue-600 border border-blue-300 px-3 py-2 rounded-md text-sm ">{`Total: ${totalItems}`}</div>
              {hasReadPermission(
                userData.permissions,
                "download_applications"
              ) && (
                <button
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-green-500 hover:bg-green-600 transition ${
                    selectedApplications.length === 0
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                  onClick={exportToExcel}
                  disabled={selectedApplications.length === 0}
                >
                  <FaFileExcel />{" "}
                  {selectedApplications.length === 0
                    ? "Download Excel"
                    : `Download Excel (${selectedApplications.length})`}
                </button>
              )}

              <select
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={itemsPerPage}
                onChange={handleLimitChange}
              >
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
                <option value="2000">2000</option>
                <option value="3000">3000</option>
                <option value="5000">5000</option>
              </select>

              {selectedApplications.length > 0 && (
                <button
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-red-500 hover:bg-red-600 transition"
                  onClick={() => setIsModalOpen(true)}
                >
                  <FaUserCheck /> Assign
                </button>
              )}

              <button
                onClick={() => setFilterModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white bg-blue-500 hover:bg-blue-600 transition"
              >
                <FaFilter /> Filter
              </button>
            </div>
          </div>

          {/* Active filter chips */}
          <div className="mb-3">
            <div className="flex flex-wrap gap-2">
              {search && (
                <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                  Search: {search}
                  <button className="ml-2" onClick={() => setSearch("")}>
                    ✕
                  </button>
                </span>
              )}
              {startDate && (
                <span className="px-3 py-1 rounded-full bg-green-100 text-green-800">
                  From: {startDate.toLocaleDateString()}
                  <button className="ml-2" onClick={() => setStartDate(null)}>
                    ✕
                  </button>
                </span>
              )}
              {endDate && (
                <span className="px-3 py-1 rounded-full bg-green-100 text-green-800">
                  To: {endDate.toLocaleDateString()}
                  <button className="ml-2" onClick={() => setEndDate(null)}>
                    ✕
                  </button>
                </span>
              )}
              {filterStatus.map((s) => (
                <span
                  key={s}
                  className="px-3 py-1 rounded-full bg-purple-100 text-purple-800"
                >
                  {s}
                  <button
                    className="ml-2"
                    onClick={() =>
                      setFilterStatus(filterStatus.filter((x) => x !== s))
                    }
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          </div>

          {loading ? (
            <SuwasthiLoader />
          ) : applications.length === 0 ? (
            <div className="bg-white shadow-md rounded-lg p-4 overflow-x-auto text-center">
              No applications found.
            </div>
          ) : (
            <div className="bg-white shadow-md rounded-lg p-4 overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-200">
                    <th className="p-3">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="p-3 text-left">Application ID</th>
                    <th className="p-3 text-left">Name</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredApps.map((app, index) => (
                    <motion.tr
                      key={app._id}
                      className={`border-b ${
                        !app.isRead ? "bg-yellow-100" : ""
                      }`}
                      variants={listVariants}
                      initial="hidden"
                      animate="visible"
                      custom={index}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedApplications.includes(app._id)}
                          onChange={() => handleCheckboxChange(app._id)}
                        />
                      </td>
                      <td className="p-3 flex items-center gap-2">
                        {(() => {
                          const createdDate = new Date(app.createdAt);
                          const today = new Date();
                          const isToday =
                            createdDate.getDate() === today.getDate() &&
                            createdDate.getMonth() === today.getMonth() &&
                            createdDate.getFullYear() === today.getFullYear();
                          return isToday ? (
                            <motion.span
                              className="bg-green-500 text-white px-2 py-1 text-xs rounded"
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{ type: "spring", stiffness: 200 }}
                            >
                              New
                            </motion.span>
                          ) : null;
                        })()}
                        {app?.id}
                      </td>
                      <td className="p-3">
                        {app?.candidateSnapshot?.firstName}{" "}
                        {app?.candidateSnapshot?.lastName}
                      </td>
                      <td className="p-3">{app.candidateSnapshot?.email}</td>
                      <td className="p-3">{app.applicationStatus}</td>
                      <td className="p-3 flex gap-2">
                        {hasPermission(
                          userData.permissions,
                          "application",
                          "read"
                        ) && (
                          <Link
                            className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                            to={`/applications/${app._id}`}
                          >
                            View
                          </Link>
                        )}
                        {hasPermission(
                          userData.permissions,
                          "application",
                          "manage"
                        ) && (
                          <Link
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                            to={`/applications/${app._id}/manage`}
                          >
                            Manage
                          </Link>
                        )}
                        {hasPermission(
                          userData.permissions,
                          "application",
                          "write"
                        ) && (
                          <Link
                            className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
                            to={`/applications/${app._id}/manage`}
                          >
                            Manage
                          </Link>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {isModalOpen && (
          <AssignRoleModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            selectedApplications={selectedApplications}
          />
        )}

        {filterModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-xl">
              <h2 className="text-xl font-semibold mb-4">
                Filter Applications
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="mb-4">
                  <label className="block text-sm mb-1">Start Date:</label>
                  <DatePicker
                    selected={startDate}
                    onChange={setStartDate}
                    placeholderText="Start Date"
                    className="p-2 border rounded w-full"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm mb-1">End Date:</label>
                  <DatePicker
                    selected={endDate}
                    onChange={setEndDate}
                    placeholderText="End Date"
                    className="p-2 border rounded w-full"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm mb-1 font-medium">
                  Status:
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {allStatuses.map((status) => (
                    <label
                      key={status}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={filterStatus.includes(status)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilterStatus([...filterStatus, status]);
                          } else {
                            setFilterStatus(
                              filterStatus.filter((s) => s !== status)
                            );
                          }
                        }}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <select
                className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                value={itemsPerPage}
                onChange={handleLimitChange}
              >
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
                <option value="2000">2000</option>
                <option value="3000">3000</option>
                <option value="5000">5000</option>
              </select>

              <div className="flex justify-end gap-2">
                <button
                  className="bg-gray-300 text-black px-4 py-2 rounded"
                  onClick={() => {
                    setStartDate(null);
                    setEndDate(null);
                    setFilterStatus([]);
                    setSearch("");
                    setCurrentPage(1);
                    setItemsPerPage(100);
                    setSearchParams({}, { replace: true });
                    fetchFilterApplications();
                  }}
                >
                  Clear Filters
                </button>

                <button
                  onClick={() => setFilterModalOpen(false)}
                  className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setFilterModalOpen(false);
                    fetchFilterApplications();
                  }}
                  className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        )}

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </Container>
    </Layout>
  );
};

export default FilterApplications;